import React from "react";
import { MessageDescriptor } from "react-intl";

import {
	Radio,
	RadioGroup,
	RadioGroupColumn
} from "src/common/components/Radio";
import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import {
	useLogProp,
	useMessageProps,
	useValue
} from "src/features/forms/hooks/useField";
import { RadioGroupProps } from "src/features/forms/types";

export const RadioGroupRenderer: React.FunctionComponent<RadioGroupProps> = ({
	direction,
	columns = 1,
	labels
}) => {
	const isLocked = React.useContext(IsLockedContext);
	const options = useOptions(labels);
	const [value, onChangeValue] = useValue<string | undefined>(undefined);

	const groups: { label: string; value: string }[][] = [];
	for (let i = 0; i < columns; i++) {
		groups[i] = [];
	}
	for (let i = 0; i < options.length; i++) {
		groups[i % columns].push(options[i]);
	}

	return (
		<RadioGroup direction={direction} columns={columns}>
			{groups.map((options, i) => (
				<RadioGroupColumn key={i}>
					{options.map(option => (
						<Radio
							key={option.value}
							disabled={isLocked}
							checked={value === option.value}
							value={option.value}
							onChangeValue={onChangeValue}
						>
							{option.label}
						</Radio>
					))}
				</RadioGroupColumn>
			))}
		</RadioGroup>
	);
};

function useOptions(messages: {
	[key: string]: MessageDescriptor;
}): { label: string; value: string }[] {
	const formatMessage = useFormatMessageMap();
	const messageProps = useMessageProps();
	const values = useLogProp<string[]>(messageProps, "values", []);
	return React.useMemo(
		() =>
			values.map(value => ({
				label: formatMessage(messages, value),
				value
			})),
		[messages, values, formatMessage]
	);
}
